import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DollarSign, CreditCard, Clock, CheckCircle, AlertCircle, Mail, FileText, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import SeoHead from '@/components/shared/SeoHead';
import { BRAND_OPERATOR_LINE, COMPANY } from '@/lib/companyInfo';

const RefundPolicy = () => {
  return (
    <>
      <SeoHead
        title="Refund Policy - Wage Atlas | Money-Back Guarantee"
        description="Wage Atlas refund policy. All salary data, tools, and reports are now free; this policy covers refunds for historical PDF report purchases."
        canonical="https://wageatlas.com/refund-policy/"
      />

      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto space-y-8">
            {/* Header */}
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center gap-2 mb-4">
                <DollarSign className="h-8 w-8 text-primary" />
                <h1 className="text-3xl font-bold">Refund Policy</h1>
              </div>
              <p className="text-lg text-muted-foreground">
                We stand behind our service with a satisfaction guarantee. Learn about our refund policy and how to request a refund.
              </p>
              <p className="text-sm text-muted-foreground">{BRAND_OPERATOR_LINE}</p>
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span>Last updated: June 2026</span>
              </div>
            </div>

            {/* Introduction Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-primary" />
                  Satisfaction Guarantee
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  At {COMPANY.brandName}, everything is now free - all salary data, tools, and PDF reports.
                  We no longer sell any paid products. This policy applies to historical purchases of the
                  previously offered $5 one-time PDF report, which were processed by {COMPANY.legalName}.
                </p>
                <div className="bg-primary/10 p-4 rounded-lg border border-primary/20">
                  <p className="text-sm font-semibold text-primary mb-2">
                    Our Promise
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Past PDF report purchases carry a 7-day money-back guarantee. If you weren't happy with a
                    purchase, we'll provide a full refund, no questions asked.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button asChild variant="outline">
                    <Link to="/refund-request">
                      Request a Refund
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* One-Time Purchase Refunds Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  One-Time Purchase Refunds (PDF Reports)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">7-Day Money-Back Guarantee</h3>
                  <p className="text-muted-foreground mb-3">
                    PDF salary reports are eligible for a full refund if requested within 7 days of purchase. 
                    We understand that sometimes a report may not meet your specific needs.
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                    <li>Full refund available within 7 days of purchase</li>
                    <li>No refunds after 7 days (digital product consumed)</li>
                    <li>Automatic processing for eligible requests</li>
                    <li>Refund processed to your original payment method</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Digital Product Policy</h3>
                  <p className="text-muted-foreground">
                    Since PDF reports are digital products that are immediately accessible upon purchase, we 
                    limit refunds to the first 7 days to prevent abuse while still ensuring customer satisfaction. 
                    If you have concerns about a PDF report, please contact our support team within the refund window.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Refund Process Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ArrowRight className="h-5 w-5 text-primary" />
                  How to Request a Refund
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">Request Process</h3>
                  <ol className="list-decimal list-inside space-y-2 text-muted-foreground ml-4">
                    <li>Log in to your Wage Atlas account</li>
                    <li>Navigate to the Refund Request page or your Dashboard billing section</li>
                    <li>Select the payment you'd like to refund</li>
                    <li>Provide a reason for the refund (optional but helpful)</li>
                    <li>Submit your refund request</li>
                  </ol>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Processing Timeline</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-muted/30 rounded-lg">
                      <h4 className="font-semibold mb-2">Automatic Processing</h4>
                      <p className="text-sm text-muted-foreground">
                        Requests within policy window are automatically approved and processed within 24-48 hours.
                      </p>
                    </div>
                    <div className="p-4 bg-muted/30 rounded-lg">
                      <h4 className="font-semibold mb-2">Manual Review</h4>
                      <p className="text-sm text-muted-foreground">
                        Requests outside policy window are reviewed by our team within 3-5 business days.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Refund Completion</h3>
                  <p className="text-muted-foreground">
                    Once processed, refunds are credited back to your original payment method through our secure
                    payment processor. Processing times vary by payment method (typically 5-10 business 
                    days for credit/debit cards). You'll receive email confirmation when your refund is processed.
                  </p>
                </div>

                <Button asChild className="mt-4">
                  <Link to="/refund-request">
                    Request a Refund Now
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>

            {/* Eligibility Criteria Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-primary" />
                  Eligibility Criteria
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">What Qualifies for Refund</h3>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                    <li>PDF reports purchased within the last 7 days</li>
                    <li>Payments with status "completed" (not already refunded)</li>
                    <li>Payments made through our official payment processor</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Exclusions and Limitations</h3>
                  <div className="bg-amber-50 dark:bg-amber-950/20 p-4 rounded-lg border border-amber-200 dark:border-amber-800">
                    <p className="text-sm text-amber-800 dark:text-amber-200 font-semibold mb-2">
                      Not Eligible for Refund
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-sm text-amber-700 dark:text-amber-300 ml-4">
                      <li>PDF reports purchased more than 7 days ago</li>
                      <li>Payments that have already been refunded</li>
                      <li>Payments with status "failed" or "cancelled"</li>
                      <li>Refunds requested for fraudulent or abusive activity</li>
                    </ul>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Special Circumstances</h3>
                  <p className="text-muted-foreground">
                    We understand that sometimes special circumstances arise. If you believe your situation 
                    warrants an exception to our standard refund policy, please contact our support team. 
                    We review each case individually and may approve refunds outside the standard policy window 
                    on a case-by-case basis.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Refund Methods Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-primary" />
                  How Refunds Are Processed
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">Payment Method Refunds</h3>
                  <p className="text-muted-foreground mb-3">
                    Refunds are processed back to your original payment method through our secure payment processor.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-muted/30 rounded-lg">
                      <h4 className="font-semibold mb-2">Credit/Debit Cards</h4>
                      <p className="text-sm text-muted-foreground">
                        Refunds appear on your statement within 5-10 business days, depending on your bank.
                      </p>
                    </div>
                    <div className="p-4 bg-muted/30 rounded-lg">
                      <h4 className="font-semibold mb-2">Other Payment Methods</h4>
                      <p className="text-sm text-muted-foreground">
                        Refunds for other payment methods are processed by our payment processor and typically appear 
                        within 5-10 business days.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Refund Timeline</h3>
                  <ol className="list-decimal list-inside space-y-2 text-muted-foreground ml-4">
                    <li><strong>Request Submitted:</strong> Immediately upon submission</li>
                    <li><strong>Approval:</strong> Within 24-48 hours (automatic) or 3-5 days (manual review)</li>
                    <li><strong>Processing:</strong> 1-2 business days after approval</li>
                    <li><strong>Credit Appears:</strong> 5-10 business days (varies by payment method)</li>
                  </ol>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">After a Refund</h3>
                  <p className="text-muted-foreground">
                    When a PDF report is refunded, the charge is reversed to your original payment method. Any report
                    file you've already downloaded remains yours to keep. All salary data and tools stay free to use.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Contact & Support Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-primary" />
                  Contact & Support
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  If you have questions about our refund policy or need assistance with a refund request, 
                  our support team is here to help.
                </p>
                <div className="space-y-2">
                  <p className="text-muted-foreground">
                    <strong>Email:</strong>{' '}
                    <a href="mailto:support@wageatlas.com" className="text-primary hover:underline">
                      support@wageatlas.com
                    </a>
                  </p>
                  <p className="text-muted-foreground">
                    <strong>Response Time:</strong> We typically respond within 24-48 hours
                  </p>
                  <p className="text-muted-foreground">
                    <strong>Refund Requests:</strong> Use our{' '}
                    <Link to="/refund-request" className="text-primary hover:underline">
                      online refund request form
                    </Link>{' '}
                    for fastest processing
                  </p>
                </div>
                <Button asChild variant="outline" className="mt-4">
                  <Link to="/contact">
                    <Mail className="mr-2 h-4 w-4" />
                    Contact Support
                  </Link>
                </Button>
              </CardContent>
            </Card>

            {/* Policy Updates Card */}
            <Card>
              <CardHeader>
                <CardTitle>Policy Updates</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  We may update this refund policy periodically to reflect changes in our practices or legal requirements. 
                  Material changes will be communicated via email or prominent notice on our website. The "Last updated" 
                  date at the top of this page indicates when the policy was last revised. Your continued use of our 
                  services after policy updates constitutes acceptance of the revised terms.
                </p>
                <div className="mt-4 p-4 bg-muted/30 rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    <strong>Effective Date:</strong> June 2026
                  </p>
                </div>
              </CardContent>
            </Card>

          </div>
        </div>
      </div>
    </>
  );
};

export default RefundPolicy;

